// @ts-nocheck
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Calendar, Send, TestTube, Users, Eye } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNewsletterSchedules, NewsletterSchedule } from '@/hooks/useNewsletterSchedules';
import NewsletterScheduleFormDialog from '@/components/admin/newsletter-schedules/NewsletterScheduleFormDialog';
import SendTestDialog from '@/components/admin/newsletter-schedules/SendTestDialog';
import NewsletterPreviewModal from '@/components/admin/newsletter-schedules/NewsletterPreviewModal';
import { EmergencyStopButton } from '@/components/admin/newsletter-schedules/EmergencyStopButton';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AudienceSegmentsContent from '@/components/admin/newsletters/AudienceSegmentsContent';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function NewsletterSchedules() {
  const { schedules, isLoading, createSchedule, updateSchedule, deleteSchedule } = useNewsletterSchedules();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<NewsletterSchedule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  const [showInlineEdit, setShowInlineEdit] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testScheduleId, setTestScheduleId] = useState<string | null>(null);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState('schedules');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewSchedule, setPreviewSchedule] = useState<NewsletterSchedule | null>(null);

  // Debug logging
  console.log('📊 Newsletter Schedules Page - Schedules:', schedules);
  console.log('📊 Newsletter Schedules Page - isLoading:', isLoading);
  console.log('📊 Newsletter Schedules Page - schedules.length:', schedules.length);

  const handleCreateClick = () => {
    setEditingSchedule(null);
    setShowInlineEdit(true);
  };

  const handleEditClick = (schedule: NewsletterSchedule) => {
    setEditingSchedule(schedule);
    setShowInlineEdit(true);
  };

  const handleFormClose = () => {
    setShowInlineEdit(false);
    setEditingSchedule(null);
  };

  const handleFormSubmit = async (data: any) => {
    if (editingSchedule) {
      await updateSchedule(editingSchedule.id, data);
    } else {
      await createSchedule(data);
    }
    handleFormClose();
  };

  const handleToggleActive = async (schedule: NewsletterSchedule) => {
    await updateSchedule(schedule.id, { is_active: !schedule.is_active });
  };

  const handleDeleteClick = (id: string) => {
    setScheduleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (scheduleToDelete) {
      await deleteSchedule(scheduleToDelete);
      setScheduleToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  const handleSendNow = async (scheduleId: string) => {
    // CRITICAL SAFETY: Get recipient count before confirming
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;

    // Fetch approximate recipient count
    const { count } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'athlete');

    const confirmed = window.confirm(
      `⚠️ WARNING: PRODUCTION EMAIL SEND\n\n` +
      `You are about to send this newsletter to approximately ${count || '???'} recipients.\n\n` +
      `Audience: ${schedule.segment_name || 'Unknown'}\n\n` +
      `This action CANNOT be undone and WILL send real emails.\n\n` +
      `Click OK to continue, or Cancel to abort.`
    );

    if (!confirmed) {
      toast.info('Send cancelled');
      return;
    }

    const doubleConfirm = window.prompt(
      `FINAL CONFIRMATION\n\nType exactly: SEND ${count} EMAILS\n\n(case sensitive)`
    );

    if (doubleConfirm !== `SEND ${count} EMAILS`) {
      toast.error('Send cancelled - confirmation text did not match');
      return;
    }

    try {
      toast.info('Queueing newsletter emails...');
      const { error } = await invokeEdgeFunction('send-scheduled-newsletters', {
        body: { schedule_id: scheduleId }
      });

      if (error) throw error;
      
      toast.success(`Newsletter queued for ${count} recipients`);
    } catch (error: any) {
      console.error('Error sending newsletter:', error);
      toast.error(error.message || 'Failed to send newsletter');
    }
  };

  const handleTestClick = (scheduleId: string) => {
    setTestScheduleId(scheduleId);
    setTestDialogOpen(true);
  };

  const handlePreviewClick = (schedule: NewsletterSchedule) => {
    setPreviewSchedule(schedule);
    setPreviewDialogOpen(true);
  };

  const handleSendTest = async (testEmail: string) => {
    if (!testScheduleId) return;

    try {
      setIsSendingTest(true);
      toast.info(`🧪 TEST MODE: Sending ONLY to ${testEmail}...`);
      
      // CRITICAL SAFETY: Explicit test_mode flag
      const { error } = await invokeEdgeFunction('send-scheduled-newsletters', {
        body: { 
          schedule_id: testScheduleId,
          test_email: testEmail,
          test_mode: true // REQUIRED for safety
        }
      });

      if (error) throw error;
      
      toast.success(`✅ Test email sent ONLY to ${testEmail}`);
      setTestDialogOpen(false);
      setTestScheduleId(null);
    } catch (error: any) {
      console.error('Error sending test newsletter:', error);
      toast.error(error.message || 'Failed to send test newsletter');
    } finally {
      setIsSendingTest(false);
    }
  };

  const formatSchedule = (dayOfWeek: number, sendTime: string) => {
    return `Every ${DAYS_OF_WEEK[dayOfWeek]} at ${sendTime}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading newsletter schedules...</div>
      </div>
    );
  }


  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">
            Newsletters
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage newsletter schedules and audience segments
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="schedules" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Schedules
          </TabsTrigger>
          <TabsTrigger value="segments" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Audience Segments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schedules" className="space-y-6">
          <div className="flex justify-end gap-2">
            <EmergencyStopButton />
            <Button onClick={handleCreateClick}>
              <Plus className="h-4 w-4 mr-2" />
              Create Newsletter
            </Button>
          </div>

      {/* Inline Edit Form */}
      {showInlineEdit && (
        <div className="border rounded-lg p-6 bg-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {editingSchedule ? 'Edit Newsletter Schedule' : 'Create Newsletter Schedule'}
            </h2>
            <Button variant="ghost" size="sm" onClick={handleFormClose}>Cancel</Button>
          </div>
          <NewsletterScheduleFormDialog
            key={editingSchedule?.id || 'new'}
            open={showInlineEdit}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            editingSchedule={editingSchedule}
          />
        </div>
      )}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Newsletter Name</TableHead>
              <TableHead>Target Segment</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Last Sent</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No newsletter schedules yet</p>
                  <p className="text-sm">Create your first automated newsletter</p>
                </TableCell>
              </TableRow>
            ) : (
              schedules.map((schedule) => {
                console.log('🎨 Rendering schedule:', schedule.id, schedule.newsletter_name);
                return (
                  <TableRow key={schedule.id}>
                    <TableCell className="font-medium">
                      {schedule.newsletter_name}
                    </TableCell>
                    <TableCell>{schedule.segment_name || 'N/A'}</TableCell>
                    <TableCell>
                      {formatSchedule(schedule.day_of_week, schedule.send_time)}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={schedule.is_active}
                        onCheckedChange={() => handleToggleActive(schedule)}
                      />
                    </TableCell>
                    <TableCell>
                      {schedule.last_sent_at 
                        ? format(new Date(schedule.last_sent_at), 'MMM d, yyyy')
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewClick(schedule)}
                          title="Preview content"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTestClick(schedule.id)}
                          title="Send test newsletter"
                        >
                          <TestTube className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendNow(schedule.id)}
                          title="Send newsletter now"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(schedule)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(schedule.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Newsletter Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this newsletter schedule? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SendTestDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        onSend={handleSendTest}
        isLoading={isSendingTest}
      />

      {previewSchedule && (
        <NewsletterPreviewModal
          open={previewDialogOpen}
          onOpenChange={setPreviewDialogOpen}
          scheduleId={previewSchedule.id}
          scheduleName={previewSchedule.newsletter_name}
          segmentName={previewSchedule.segment_name}
          sport={previewSchedule.sport}
        />
      )}
        </TabsContent>

        <TabsContent value="segments">
          <AudienceSegmentsContent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
